'use client';
import React, { useRef, useEffect, useState } from 'react';

const processSteps = [
  {
    number: '01',
    title: 'Consultation',
    description: 'Begin with a free discovery call to understand your needs, concerns, and healing goals. We listen with full presence and compassion.',
    icon: '📞',
  },
  {
    number: '02',
    title: 'Assessment',
    description: 'A thorough energetic and emotional assessment to identify root causes, karmic patterns, and the most effective healing approach for you.',
    icon: '🔍',
  },
  {
    number: '03',
    title: 'Personalized Healing Plan',
    description: 'A customized healing roadmap designed specifically for your unique needs, combining the most suitable modalities for deep transformation.',
    icon: '📋',
  },
  {
    number: '04',
    title: 'Healing Sessions',
    description: 'Experience powerful, personalized healing sessions in a safe and sacred space — conducted online or in-person with full presence and care.',
    icon: '✨',
  },
  {
    number: '05',
    title: 'Follow-up & Transformation',
    description: 'Ongoing support, integration guidance, and follow-up sessions to anchor your healing and sustain your transformation journey.',
    icon: '🌟',
  },
];

export default function ProcessSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref?.current) observer?.observe(ref?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      id="process"
      className="relative py-24 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0d0818 0%, #130a24 50%, #0d0818 100%)' }}
    >
      <div className="absolute inset-0 lotus-bg opacity-35 pointer-events-none" />
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 glow-orb-lavender" style={{ width: '450px', height: '450px', opacity: 0.4 }} />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div
          ref={ref}
          className={`transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#c9a84c]/60" />
              <span className="text-[#c9a84c] text-xs tracking-[0.4em] uppercase font-medium">The Healing Journey</span>
              <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#c9a84c]/60" />
            </div>
            <h2
              className="font-serif text-4xl md:text-5xl font-bold mb-4"
              style={{
                background: 'linear-gradient(135deg, #c4b5fd 0%, #9b7fe8 50%, #c9a84c 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Healing Process
            </h2>
            <div className="section-divider mb-4" />
          </div>

          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {processSteps?.map((step, index) => (
              <div
                key={step?.number}
                className="relative text-center"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.7s ease ${index * 150}ms, transform 0.7s ease ${index * 150}ms`,
                }}
              >
                {/* Connector line */}
                {index < processSteps?.length - 1 && (
                  <div
                    className="hidden lg:block absolute top-8 left-1/2 w-full h-px"
                    style={{ background: 'linear-gradient(to right, rgba(201,168,76,0.4), rgba(201,168,76,0.1))' }}
                  />
                )}

                {/* Step circle */}
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center"
                    style={{
                      background: 'linear-gradient(135deg, rgba(201,168,76,0.12) 0%, rgba(124,92,191,0.18) 100%)',
                      border: '1.5px solid rgba(201,168,76,0.40)',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                    }}
                  >
                    <span className="text-2xl">{step?.icon}</span>
                  </div>
                  <div
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                    style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c96a)', color: '#0d0818' }}
                  >
                    {step?.number}
                  </div>
                </div>

                <h3 className="font-serif text-base font-semibold mb-3 text-[#e8dff5]">
                  {step?.title}
                </h3>
                <p className="text-[#c4b5fd]/60 text-xs leading-relaxed">{step?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
